import { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../../helper/ProtectedRoute';
import { useCodAutomationStore } from '../../../store/CodAutomation/codAutomationStore';
import EyeIcon from '../../../assets/img/eye_show.svg';
import templateIcon from '../../../assets/img/template_icon.svg';
import { showErrorToast, showSuccessToast } from '../../../components/Toast/Toaster';
import fileDownload from 'js-file-download';
import dayjs from 'dayjs';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';

const TableComponent = ({
  tableData,
  sortField,
  sorting,
  restrict,
  newPage,
  size,
  selectedRows,
  setSelectedRows,
  onViewClick,
  fetchPageData,
}) => {
  const getCodPayableExcel = useCodAutomationStore((state) => state?.getCodPayableExcel);
  const downloadBankReceipt = useCodAutomationStore((state) => state?.downloadBankReceipt);

  const [state] = useContext(UserContext);
  const [role, setRole] = useState(sessionStorage.getItem('user_access_role'));

  useEffect(() => {
    setRole(state.roles);
  }, [state.roles]);

  const handleSelectRow = (summaryId) => {
    if (selectedRows.includes(summaryId)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== summaryId));
    } else {
      setSelectedRows([...selectedRows, summaryId]);
    }
  };

  const isRowSelected = (summaryId) => selectedRows.includes(summaryId);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(tableData.map((row) => row?.summaryId));
    } else {
      setSelectedRows([]);
    }
  };

  const SortIcon = ({ field }) => {
    const currentField = sortField?.startsWith('-') ? sortField.slice(1) : sortField;
    if (currentField !== field) return null;
    return sortField?.startsWith('-')
      ? <ChevronDown className="size-3 inline ml-1" />
      : <ChevronUp className="size-3 inline ml-1" />;
  };

  const colCount =
    2 +
    (!restrict?.includes('Customer Account') ? 1 : 0) +
    (!restrict?.includes('Customer Name') ? 1 : 0) +
    (!restrict?.includes('Bank Charges') ? 1 : 0) +
    (!restrict?.includes('Payable Amount') ? 1 : 0) +
    (!restrict?.includes('Email Sent') ? 1 : 0) +
    (!restrict?.includes('From Date') ? 1 : 0) +
    (!restrict?.includes('To Date') ? 1 : 0) +
    (!restrict?.includes('Generated On') ? 1 : 0) +
    (!restrict?.includes('Generated') ? 1 : 0) +
    (!restrict?.includes('Generated By') ? 1 : 0) +
    (!restrict?.includes('Email Sent By') ? 1 : 0) +
    (!restrict?.includes('Destination Country') ? 1 : 0) +
    (!restrict?.includes('Status') ? 1 : 0) +
    (!restrict?.includes('Reconciled At') ? 1 : 0) +
    (!restrict?.includes('Reconciled By') ? 1 : 0) +
    (!restrict?.includes('Action') ? 1 : 0);

  return (
    <>
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="tbl-th sticky top-0 left-0 z-30 bg-gray-50 w-10">
            <div className="smallCheckbox">
              <input
                type="checkbox"
                id="tableCheckall"
                checked={tableData?.length > 0 && selectedRows?.length === tableData?.length}
                onChange={handleSelectAll}
              />
              <label htmlFor="tableCheckall" />
            </div>
          </th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('createdAt')}>
            # <SortIcon field="createdAt" />
          </th>
          {!restrict?.includes('Customer Account') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('customer')}>
              Customer Account <SortIcon field="customer" />
            </th>
          )}
          {!restrict?.includes('Customer Name') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('customerName')}>
              Customer Name <SortIcon field="customerName" />
            </th>
          )}
          {!restrict?.includes('Bank Charges') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">
              Bank Charges
            </th>
          )}
          {!restrict?.includes('Payable Amount') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('payable')}>
              Payable Amount <SortIcon field="payable" />
            </th>
          )}
          {!restrict?.includes('Email Sent') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('emailSent')}>
              Email Sent <SortIcon field="emailSent" />
            </th>
          )}
          {!restrict?.includes('From Date') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">From Date</th>
          )}
          {!restrict?.includes('To Date') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">To Date</th>
          )}
          {!restrict?.includes('Generated On') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('generatedOn')}>
              Generated On <SortIcon field="generatedOn" />
            </th>
          )}
          {!restrict?.includes('Generated') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('generated')}>
              Generated <SortIcon field="generated" />
            </th>
          )}
          {!restrict?.includes('Generated By') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">Generated By</th>
          )}
          {!restrict?.includes('Email Sent By') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">Email Sent By</th>
          )}
          {!restrict?.includes('Destination Country') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('destinationCountryCode')}>
              Destination Country <SortIcon field="destinationCountryCode" />
            </th>
          )}
          {!restrict?.includes('Status') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('status')}>
              Status <SortIcon field="status" />
            </th>
          )}
          {!restrict?.includes('Reconciled At') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('reconciledAt')}>
              Reconciled At <SortIcon field="reconciledAt" />
            </th>
          )}
          {!restrict?.includes('Reconciled By') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">Reconciled By</th>
          )}
          {!restrict?.includes('Action') && (
            <th className="tbl-th sticky top-0 right-0 z-20 bg-gray-50 text-left" style={{ minWidth: '120px' }}>Action</th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {!tableData?.length ? (
          <tr>
            <td colSpan={colCount} className="px-6 py-12 text-center text-gray-500">No records found</td>
          </tr>
        ) : (
          tableData.map((item, i) => (
            <tr
              key={item?.summaryId}
              className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                selectedRows?.includes(item?.summaryId)
                  ? 'bg-red-50'
                  : i % 2 === 0
                  ? 'bg-white'
                  : 'bg-gray-50/50'
              }`}
            >
              <td className="tbl-td sticky left-0 z-10 bg-white">
                <div className="smallCheckbox">
                  <input
                    type="checkbox"
                    id={`tableCheck${i}`}
                    checked={isRowSelected(item?.summaryId)}
                    onChange={() => handleSelectRow(item?.summaryId)}
                  />
                  <label htmlFor={`tableCheck${i}`} />
                </div>
              </td>
              <td className="tbl-td">{newPage * size + (i + 1)}</td>
              {!restrict?.includes('Customer Account') && <td className="tbl-td">{item?.customerAccount}</td>}
              {!restrict?.includes('Customer Name') && <td className="tbl-td">{item?.customerName}</td>}
              {!restrict?.includes('Bank Charges') && <td className="tbl-td">{item?.bankCharges ?? '-'}</td>}
              {!restrict?.includes('Payable Amount') && <td className="tbl-td">{item?.payable}</td>}
              {!restrict?.includes('Email Sent') && <td className="tbl-td">{item?.emailSent ? 'Yes' : 'No'}</td>}
              {!restrict?.includes('From Date') && (
                <td className="tbl-td">
                  {item?.from ? dayjs(item?.from).format('DD MMM YYYY, hh:mm:ss A') : null}
                </td>
              )}
              {!restrict?.includes('To Date') && (
                <td className="tbl-td">
                  {item?.to ? dayjs(item?.to).format('DD MMM YYYY, hh:mm:ss A') : null}
                </td>
              )}
              {!restrict?.includes('Generated On') && (
                <td className="tbl-td">
                  {item?.generatedOn ? dayjs(item?.generatedOn).format('DD MMM YYYY, hh:mm:ss A') : null}
                </td>
              )}
              {!restrict?.includes('Generated') && (
                <td className="tbl-td">{item?.generated ? 'Yes' : 'No'}</td>
              )}
              {!restrict?.includes('Generated By') && (
                <td className="tbl-td">{item?.generatedBy ?? '-'}</td>
              )}
              {!restrict?.includes('Email Sent By') && (
                <td className="tbl-td">{item?.emailSentBy ?? '-'}</td>
              )}
              {!restrict?.includes('Destination Country') && (
                <td className="tbl-td">{item?.destinationCountryCode ?? '-'}</td>
              )}
              {!restrict?.includes('Status') && (
                <td className="tbl-td">{item?.status ?? '-'}</td>
              )}
              {!restrict?.includes('Reconciled At') && (
                <td className="tbl-td">
                  {item?.reconciledAt ? dayjs(Number(item?.reconciledAt)).format('DD MMM YYYY, hh:mm:ss A') : null}
                </td>
              )}
              {!restrict?.includes('Reconciled By') && (
                <td className="tbl-td">{item?.reconciledBy ?? '-'}</td>
              )}
              {!restrict?.includes('Action') && (
                <td className="tbl-td sticky right-0 z-10 bg-white" style={{ minWidth: '120px' }}>
                  <div className="flex items-center justify-start gap-1">
                    <button
                      className="p-1.5 bg-white hover:!bg-gray-100 rounded transition-colors"
                      title="View"
                      onClick={() => onViewClick({ customer: item?.customerAccount, from: item?.from, to: item?.to, destinationCountry: item?.destinationCountryCode, summaryItem: item })}
                    >
                      <img src={EyeIcon} className="h-4 w-4 turnBlack" alt="view" />
                    </button>
                    <button
                      className="p-1.5 bg-white hover:!bg-gray-100 rounded transition-colors"
                      title="Download Excel"
                      onClick={async () => {
                        try {
                          const response = await getCodPayableExcel(item?.summaryId);
                          fileDownload(response?.data, `${item?.customerAccount}.csv`);
                        } catch (error) {
                          showErrorToast(error?.msg || 'ERROR');
                        }
                      }}
                    >
                      <img src={templateIcon} className="h-4 w-4 turnBlack" alt="download" />
                    </button>
                    {item?.receiptUrl && (
                      <button
                        className="p-1.5 bg-white hover:!bg-gray-100 rounded transition-colors"
                        title="Download Bank Receipt"
                        onClick={async () => {
                          const response = await downloadBankReceipt(item?.summaryId, item?.customerAccount);
                          if (response?.data) {
                            const ext = item?.fileType?.split('/')[1] || 'pdf';
                            fileDownload(response?.data, `bank-receipt-${item?.customerAccount}.${ext}`);
                          } else {
                            showErrorToast('No bank receipt found for this record');
                          }
                        }}
                      >
                        <Download className="h-4 w-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </>
  );
};

export default TableComponent;
